import React, { useEffect, useMemo, useState } from 'react';
import { pmAdminApi, CRM_COMPANY_TYPES, CRM_LEAD_STATUSES } from '../../lib/pmApi';
import { Briefcase, Plus, Search, CheckCircle2, Archive, History as HistoryIcon, ExternalLink } from 'lucide-react';

const emptyForm = {
  company_name: '', main_contact_name: '', contact_title: '',
  phone: '', email: '', website: '',
  address: '', city: '', state: '', zip: '',
  company_type: 'Property Management', lead_status: 'Prospect',
  vendor_packet_submitted: false, vendor_approved: false,
  insurance_certificate_sent: false, w9_sent: false, pricing_sheet_sent: false,
  last_contact_date: '', next_followup_date: '',
  estimated_units: 0, estimated_properties: 0,
  notes: '', notes_internal: '',
};

const STATUS_STYLE = {
  'Prospect':           'bg-[#262626] text-[#A3A3A3]',
  'Contacted':          'bg-[#3B82F6]/15 text-[#60A5FA]',
  'Vendor Packet Sent': 'bg-[#D4AF37]/15 text-[#D4AF37]',
  'In Review':          'bg-[#F59E0B]/15 text-[#F59E0B]',
  'Approved Vendor':    'bg-[#10B981]/15 text-[#10B981]',
  'Active Customer':    'bg-[#D4AF37]/30 text-[#F3E5AB] border border-[#D4AF37]',
  'Not Interested':     'bg-[#525252]/30 text-[#525252]',
};

const Widget = ({ label, value, testId, accent }) => (
  <div data-testid={testId} className="bg-[#141414] border border-[#262626] rounded-md p-4 min-w-[150px]">
    <p className="text-[10px] font-bold tracking-[0.2em] text-[#A3A3A3] mb-1">{label}</p>
    <p className={`text-3xl font-['Outfit'] font-bold ${accent || 'text-white'}`}>{value}</p>
  </div>
);

const PmCrm = () => {
  const [list, setList] = useState([]);
  const [widgets, setWidgets] = useState(null);
  const [filters, setFilters] = useState({ status: '', q: '', include_archived: false });
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [openAudit, setOpenAudit] = useState(null);
  const [audit, setAudit] = useState([]);

  const load = () => Promise.all([
    pmAdminApi.crm.list({ ...(filters.status && { status: filters.status }), ...(filters.q && { q: filters.q }), include_archived: filters.include_archived }),
    pmAdminApi.crm.widgets(),
  ]).then(([rows, w]) => { setList(rows); setWidgets(w); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.status, filters.include_archived]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [filters.q]);

  const startNew = () => { setEdit('new'); setForm(emptyForm); };
  const startEdit = (p) => {
    setEdit(p.id);
    setForm({
      ...emptyForm,
      ...p,
      last_contact_date: p.last_contact_date || '',
      next_followup_date: p.next_followup_date || '',
      estimated_units: p.estimated_units || 0,
      estimated_properties: p.estimated_properties || 0,
    });
  };
  const cancelEdit = () => { setEdit(null); setForm(emptyForm); };

  const save = async () => {
    const body = { ...form, estimated_units: Number(form.estimated_units) || 0, estimated_properties: Number(form.estimated_properties) || 0 };
    if (edit === 'new') await pmAdminApi.crm.create(body);
    else await pmAdminApi.crm.update(edit, body);
    cancelEdit(); load();
  };
  const archive = async (p) => { if (window.confirm(`Archive ${p.company_name}?`)) { await pmAdminApi.crm.archive(p.id); load(); } };
  const convert = async (p) => {
    if (!window.confirm(`Convert "${p.company_name}" into an active Property Management Company?\n\nThis will create a live company in the Property Manager Portal.`)) return;
    const r = await pmAdminApi.crm.convert(p.id);
    if (r.already_converted) {
      alert(`Already converted (company ${r.company_id}).`);
    } else {
      alert(`Converted! New company code: ${r.code}. Visit /admin/pm/companies to assign properties.`);
    }
    load();
  };
  const showAudit = async (p) => {
    setOpenAudit(p.id);
    const rows = await pmAdminApi.crm.audit(p.id);
    setAudit(rows);
  };

  const visible = useMemo(() => list, [list]);

  return (
    <div data-testid="admin-pm-crm" className="p-6 max-w-7xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-1">PROPERTY MGMT · CRM</p>
          <h1 className="font-['Outfit'] text-3xl font-bold text-white">Prospect Tracking</h1>
          <p className="text-[#A3A3A3] text-sm mt-1">Track property management companies through onboarding — from first contact to active customer.</p>
        </div>
        <button data-testid="crm-new-btn" onClick={startNew} className="bg-[#D4AF37] text-[#0A0A0A] px-4 py-2 rounded font-medium hover:bg-[#F3E5AB] inline-flex items-center gap-2"><Plus className="w-4 h-4" /> New Prospect</button>
      </div>

      {/* Dashboard widgets */}
      {widgets && (
        <div data-testid="crm-widgets" className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Widget testId="crm-widget-prospects"        label="PM PROSPECTS"           value={widgets.pm_prospects} />
          <Widget testId="crm-widget-packets"          label="VENDOR PACKETS PENDING" value={widgets.vendor_packets_pending} accent="text-[#D4AF37]" />
          <Widget testId="crm-widget-followups"        label="FOLLOW-UPS DUE"         value={widgets.followups_due} accent={widgets.followups_due > 0 ? 'text-[#EF4444]' : 'text-white'} />
          <Widget testId="crm-widget-approved"         label="APPROVED VENDORS"       value={widgets.approved_vendors} accent="text-[#10B981]" />
          <Widget testId="crm-widget-active-companies" label="ACTIVE PM COMPANIES"    value={widgets.active_pm_companies} accent="text-[#F3E5AB]" />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-[#525252]" />
          <input data-testid="crm-filter-search" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Search company, contact, email, phone…" className="bg-[#0A0A0A] border border-[#262626] rounded h-10 pl-9 pr-3 text-white text-sm w-72" />
        </div>
        <select data-testid="crm-filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm">
          <option value="">All statuses</option>
          {CRM_LEAD_STATUSES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <label className="text-[#A3A3A3] text-xs flex items-center gap-1">
          <input data-testid="crm-filter-archived" type="checkbox" checked={filters.include_archived} onChange={(e) => setFilters({ ...filters, include_archived: e.target.checked })} className="accent-[#D4AF37]" /> Include archived
        </label>
      </div>

      {/* Table */}
      <div className="bg-[#141414] border border-[#262626] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-[#A3A3A3]">
            <tr>
              <th className="p-3 text-left">Company</th>
              <th className="p-3 text-left">Contact</th>
              <th className="p-3 text-left">Type</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">Units / Props</th>
              <th className="p-3 text-left">Next Follow-Up</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => (
              <tr key={p.id} data-testid={`crm-row-${p.id}`} className={`border-t border-[#262626] hover:bg-[#1a1a1a] ${p.archived ? 'opacity-40' : ''}`}>
                <td className="p-3">
                  <p className="text-white font-medium">{p.company_name}</p>
                  <p className="text-[#525252] text-xs">{p.website || ''}</p>
                </td>
                <td className="p-3">
                  <p className="text-white">{p.main_contact_name || '—'}</p>
                  <p className="text-[#525252] text-xs">{p.contact_title || ''}{p.phone ? ` · ${p.phone}` : ''}</p>
                </td>
                <td className="p-3 text-[#A3A3A3] text-xs">{p.company_type}</td>
                <td className="p-3"><span className={`text-xs font-medium px-2 py-1 rounded ${STATUS_STYLE[p.lead_status] || 'bg-[#262626] text-[#A3A3A3]'}`}>{p.lead_status}</span></td>
                <td className="p-3 text-[#A3A3A3] text-xs">{p.estimated_units || 0} / {p.estimated_properties || 0}</td>
                <td className={`p-3 text-xs ${p.next_followup_date && p.next_followup_date <= new Date().toISOString().slice(0, 10) ? 'text-[#EF4444]' : 'text-[#A3A3A3]'}`}>{p.next_followup_date || '—'}</td>
                <td className="p-3 text-right space-x-2 whitespace-nowrap">
                  <button data-testid={`crm-edit-${p.id}`} onClick={() => startEdit(p)} className="text-[#A3A3A3] hover:text-[#D4AF37] text-xs">Edit</button>
                  <button onClick={() => showAudit(p)} className="text-[#A3A3A3] hover:text-[#D4AF37]" title="View audit"><HistoryIcon className="w-3.5 h-3.5 inline-block" /></button>
                  {!p.converted_company_id && (
                    <button data-testid={`crm-convert-${p.id}`} onClick={() => convert(p)} className="text-[#10B981] hover:text-[#34D399] text-xs inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Convert</button>
                  )}
                  {p.converted_company_id && <span className="text-[#10B981] text-xs inline-flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5" /> Active</span>}
                  {!p.archived && <button onClick={() => archive(p)} className="text-[#525252] hover:text-[#EF4444]"><Archive className="w-3.5 h-3.5 inline-block" /></button>}
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-[#525252]">
                <Briefcase className="w-6 h-6 mx-auto mb-2 text-[#525252]" />
                No prospects yet. Click <span className="text-[#D4AF37]">New Prospect</span> to start tracking your first one.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Form drawer */}
      {edit && (
        <div className="fixed inset-0 z-40 bg-black/60 flex items-end sm:items-center justify-center p-2 sm:p-6" onClick={cancelEdit}>
          <div className="bg-[#141414] border border-[#262626] rounded-md max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-[#262626] flex items-center justify-between">
              <p className="text-white font-medium">{edit === 'new' ? 'New Prospect' : `Edit Prospect`}</p>
              <button onClick={cancelEdit} className="text-[#A3A3A3] hover:text-white">✕</button>
            </div>
            <div className="p-5 space-y-3">
              <input data-testid="crm-form-company" placeholder="Company name *" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
              <div className="grid sm:grid-cols-2 gap-2">
                <input data-testid="crm-form-contact" placeholder="Main contact name" value={form.main_contact_name} onChange={(e) => setForm({ ...form, main_contact_name: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
                <input placeholder="Title" value={form.contact_title} onChange={(e) => setForm({ ...form, contact_title: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
                <input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
              </div>
              <input placeholder="Website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
              <input placeholder="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
              <div className="grid grid-cols-3 gap-2">
                <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
                <input placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
                <input placeholder="Zip" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <select data-testid="crm-form-type" value={form.company_type} onChange={(e) => setForm({ ...form, company_type: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm">
                  {CRM_COMPANY_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
                <select data-testid="crm-form-status" value={form.lead_status} onChange={(e) => setForm({ ...form, lead_status: e.target.value })} className="bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm">
                  {CRM_LEAD_STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>

              <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.2em] pt-2">VENDOR ONBOARDING</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-white">
                {[
                  ['vendor_packet_submitted', 'Vendor Packet Submitted'],
                  ['vendor_approved',         'Vendor Approved'],
                  ['insurance_certificate_sent', 'Insurance Cert Sent'],
                  ['w9_sent',                 'W-9 Sent'],
                  ['pricing_sheet_sent',      'Pricing Sheet Sent'],
                ].map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 p-2 bg-[#0A0A0A] border border-[#262626] rounded cursor-pointer">
                    <input data-testid={`crm-form-${k}`} type="checkbox" checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} className="accent-[#D4AF37]" />
                    {label}
                  </label>
                ))}
              </div>

              <p className="text-[#A3A3A3] text-xs font-bold tracking-[0.2em] pt-2">FOLLOW-UP &amp; SCALE</p>
              <div className="grid sm:grid-cols-2 gap-2">
                <label className="text-[#A3A3A3] text-xs">Last contact date
                  <input type="date" value={form.last_contact_date || ''} onChange={(e) => setForm({ ...form, last_contact_date: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
                </label>
                <label className="text-[#A3A3A3] text-xs">Next follow-up date
                  <input data-testid="crm-form-followup" type="date" value={form.next_followup_date || ''} onChange={(e) => setForm({ ...form, next_followup_date: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
                </label>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <label className="text-[#A3A3A3] text-xs">Estimated units
                  <input type="number" min="0" value={form.estimated_units} onChange={(e) => setForm({ ...form, estimated_units: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
                </label>
                <label className="text-[#A3A3A3] text-xs">Estimated properties
                  <input type="number" min="0" value={form.estimated_properties} onChange={(e) => setForm({ ...form, estimated_properties: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded h-10 px-3 text-white text-sm" />
                </label>
              </div>

              <textarea placeholder="Notes (shareable internally)" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-3 text-white text-sm" />
              <textarea data-testid="crm-form-internal" placeholder="📌 Internal NexFlow notes (never shown to external)" rows={3} value={form.notes_internal} onChange={(e) => setForm({ ...form, notes_internal: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-3 text-white text-sm" />

              <button data-testid="crm-form-save" onClick={save} disabled={!form.company_name.trim()} className="w-full bg-[#D4AF37] text-[#0A0A0A] h-11 rounded font-medium hover:bg-[#F3E5AB] disabled:opacity-40">{edit === 'new' ? 'Create Prospect' : 'Save changes'}</button>
              <button onClick={cancelEdit} className="w-full text-[#525252] text-xs">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Audit modal */}
      {openAudit && (
        <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4" onClick={() => setOpenAudit(null)}>
          <div className="bg-[#141414] border border-[#262626] rounded-md max-w-xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-[#262626] flex items-center justify-between">
              <p className="text-white font-medium">Audit Trail</p>
              <button onClick={() => setOpenAudit(null)} className="text-[#A3A3A3] hover:text-white">✕</button>
            </div>
            <ul className="p-3 space-y-2">
              {audit.map((a) => (
                <li key={a.id} className="bg-[#0A0A0A] border border-[#262626] rounded p-3 text-sm">
                  <div className="flex justify-between gap-3">
                    <span className="text-[#D4AF37] font-mono text-xs">{a.action}</span>
                    <span className="text-[#525252] text-xs">{new Date(a.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-white mt-1">{a.actor_name}</p>
                  {Object.keys(a.details || {}).length > 0 && <pre className="text-[#A3A3A3] text-xs mt-1 whitespace-pre-wrap">{JSON.stringify(a.details, null, 2)}</pre>}
                </li>
              ))}
              {audit.length === 0 && <p className="text-[#525252] text-sm">No events.</p>}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default PmCrm;
